import { useEffect, useState } from 'react'
import * as adminApi from '../../api/admin'
import Spinner from '../../components/common/Spinner'
import { Badge, Card, EmptyState, styles, tokens } from './_ui'

const PERIODS: { days: number; label: string }[] = [
  { days: 1, label: '24h' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
]

interface Props {
  merchantId: string
}

export default function ShopAnalyticsSection({ merchantId }: Props) {
  const [days, setDays] = useState(7)
  const [data, setData] = useState<adminApi.MerchantAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    adminApi
      .getCrowdMerchantAnalytics(merchantId, days)
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load analytics')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [merchantId, days])

  const periodSwitcher = (
    <div style={{ display: 'flex', gap: 6 }}>
      {PERIODS.map((p) => {
        const active = p.days === days
        return (
          <button
            key={p.days}
            onClick={() => setDays(p.days)}
            style={{
              padding: '4px 10px',
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              background: active ? tokens.link : tokens.pageBg,
              color: active ? '#fff' : tokens.text,
              opacity: active ? 1 : 0.7,
            }}
          >
            {p.label}
          </button>
        )
      })}
    </div>
  )

  if (loading || !data) {
    return (
      <Card title="Statistics" action={periodSwitcher}>
        {error ? (
          <EmptyState emoji="⚠️" title={error} />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <Spinner />
          </div>
        )}
      </Card>
    )
  }

  const uniqueVisitorsSeries =
    data.merchant_page_unique_visitors_over_time?.length
      ? data.merchant_page_unique_visitors_over_time
      : data.merchant_page_views_over_time ?? []
  const productViewsSeries = data.views_over_time ?? []
  const uniqueVisitors =
    data.merchant_page_unique_visitors ?? data.merchant_page_views ?? data.total_views ?? 0
  const productViews = data.total_views ?? 0

  return (
    <Card title="Statistics" action={periodSwitcher}>
      {/* KPI tiles */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <KpiTile label="Unique visitors" value={uniqueVisitors} />
        <KpiTile label="Product views" value={productViews} />
        <KpiTile label="Likes" value={data.total_likes_period} />
        <KpiTile
          label="Conversion"
          value={`${(data.conversion_rate ?? 0).toFixed(1)}%`}
          hint="likes / views"
        />
        <KpiTile
          label="Repeat visitors"
          value={
            data.repeat_visitor_rate != null ? `${data.repeat_visitor_rate.toFixed(0)}%` : '—'
          }
          hint={
            data.repeat_visitor_count != null ? `${data.repeat_visitor_count} users` : undefined
          }
        />
        <KpiTile
          label="Page views"
          value={data.merchant_page_views ?? 0}
          hint="raw shop opens"
        />
      </div>

      {/* Charts */}
      <ChartBlock title="Unique visitors / day" data={uniqueVisitorsSeries} color={tokens.link} />
      <ChartBlock
        title="Product views / day"
        data={productViewsSeries}
        color={tokens.link}
        opacity={0.45}
      />

      {/* Funnels */}
      {(data.funnel_via_shop || data.funnel_via_direct) && (
        <div style={{ marginTop: 14 }}>
          <div style={styles.sectionTitle}>Funnels (distinct users)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {data.funnel_via_shop && (
              <FunnelBlock
                title="Via shop"
                stages={[
                  ['Visited shop', data.funnel_via_shop.visited_shop],
                  ['Opened product', data.funnel_via_shop.opened_product],
                  ['Favorited', data.funnel_via_shop.added_to_favorites],
                  ['Contacted', data.funnel_via_shop.messaged_seller],
                ]}
              />
            )}
            {data.funnel_via_direct && (
              <FunnelBlock
                title="Via direct link"
                stages={[
                  ['Opened product', data.funnel_via_direct.opened_product],
                  ['Favorited', data.funnel_via_direct.added_to_favorites],
                  ['Contacted', data.funnel_via_direct.messaged_seller],
                ]}
              />
            )}
          </div>
        </div>
      )}

      {/* Source + platform breakdowns */}
      {(data.source_breakdown || data.platform_breakdown) && (
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {data.source_breakdown && (
            <BreakdownBlock title="Sources" entries={data.source_breakdown} />
          )}
          {data.platform_breakdown && (
            <BreakdownBlock
              title="Platforms"
              entries={data.platform_breakdown as unknown as Record<string, number>}
            />
          )}
        </div>
      )}

      {/* Top products */}
      {data.top_viewed_products.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={styles.sectionTitle}>Top products</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.top_viewed_products.slice(0, 5).map((p) => (
              <div
                key={p.product_id}
                style={{ ...styles.innerTile, display: 'flex', alignItems: 'center', gap: 10 }}
              >
                {p.thumbnail_url ? (
                  <img
                    src={p.thumbnail_url}
                    alt=""
                    style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: tokens.cardBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      opacity: 0.4,
                    }}
                  >
                    📦
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      ...styles.text,
                      fontSize: 12,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.name}
                  </div>
                  <div style={{ ...styles.subtle, fontSize: 11, marginTop: 2 }}>
                    {p.views} views · {p.likes} likes
                    {p.conversion_rate != null && ` · ${p.conversion_rate.toFixed(1)}%`}
                  </div>
                </div>
                {p.top_source && <Badge>{p.top_source}</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top inbound searches */}
      {data.top_inbound_searches && data.top_inbound_searches.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={styles.sectionTitle}>Top inbound searches</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {data.top_inbound_searches.slice(0, 8).map((s) => (
              <div
                key={s.query}
                style={{
                  ...styles.innerTile,
                  padding: '8px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <span
                  style={{
                    ...styles.text,
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.query}
                </span>
                <span style={{ ...styles.subtle, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                  {s.view_count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

function KpiTile({
  label,
  value,
  hint,
}: {
  label: string
  value: number | string
  hint?: string
}) {
  const display = typeof value === 'number' ? value.toLocaleString('en-US') : value
  return (
    <div style={styles.innerTile}>
      <div style={{ ...styles.subtle, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ ...styles.text, fontSize: 20, fontWeight: 700, marginTop: 2, lineHeight: 1.1 }}>
        {display}
      </div>
      {hint && (
        <div style={{ ...styles.faint, fontSize: 10, marginTop: 2 }}>
          {hint}
        </div>
      )}
    </div>
  )
}

function ChartBlock({
  title,
  data,
  color,
  opacity = 0.85,
}: {
  title: string
  data: adminApi.MerchantAnalyticsTimeSeriesPoint[]
  color: string
  opacity?: number
}) {
  if (!data.length) return null
  const max = Math.max(1, ...data.map((p) => p.count))
  const total = data.reduce((s, p) => s + p.count, 0)
  const avg = total / data.length
  const height = 80
  const dense = data.length > 14
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 4,
          gap: 8,
        }}
      >
        <span style={{ ...styles.subtle, fontSize: 11 }}>{title}</span>
        <span style={{ ...styles.faint, fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
          max {max.toLocaleString('en-US')} · avg {avg.toFixed(avg < 10 ? 1 : 0)} · total{' '}
          {total.toLocaleString('en-US')}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: data.length > 10 ? 2 : 4,
          height,
        }}
      >
        {data.map((p, i) => {
          const pct = Math.max(4, (p.count / max) * 100)
          const showLabel = !dense && p.count > 0
          return (
            <div
              key={i}
              title={`${p.date}: ${p.count}`}
              style={{
                flex: 1,
                height: `${pct}%`,
                background: color,
                opacity,
                borderRadius: 4,
                position: 'relative',
              }}
            >
              {showLabel && (
                <div
                  style={{
                    position: 'absolute',
                    top: -14,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontSize: 9,
                    fontWeight: 600,
                    color: tokens.text,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  {p.count}
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          ...styles.faint,
          fontSize: 10,
          marginTop: 4,
        }}
      >
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  )
}

function FunnelBlock({ title, stages }: { title: string; stages: [string, number][] }) {
  const top = Math.max(1, ...stages.map(([, v]) => v))
  return (
    <div style={styles.innerTile}>
      <div style={{ ...styles.subtle, fontSize: 10, textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.06em' }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {stages.map(([label, value], i) => {
          const pct = Math.max(6, (value / top) * 100)
          return (
            <div key={i} style={{ position: 'relative' }}>
              <div
                style={{
                  height: 22,
                  width: `${pct}%`,
                  background: tokens.link,
                  opacity: 0.18 + 0.18 * (stages.length - i),
                  borderRadius: 6,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 8px',
                  fontSize: 11,
                  color: tokens.text,
                }}
              >
                <span style={{ opacity: 0.85 }}>{label}</span>
                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function BreakdownBlock({ title, entries }: { title: string; entries: Record<string, number> }) {
  const items = Object.entries(entries).filter(([, v]) => (v ?? 0) > 0)
  if (items.length === 0) {
    return (
      <div style={styles.innerTile}>
        <div style={{ ...styles.subtle, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {title}
        </div>
        <div style={{ ...styles.faint, fontSize: 11, marginTop: 4 }}>No data</div>
      </div>
    )
  }
  const total = items.reduce((s, [, v]) => s + v, 0)
  return (
    <div style={styles.innerTile}>
      <div style={{ ...styles.subtle, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items
          .sort((a, b) => b[1] - a[1])
          .map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
              <span style={{ ...styles.text }}>{k}</span>
              <span style={{ ...styles.subtle, fontVariantNumeric: 'tabular-nums' }}>
                {v} · {((v / total) * 100).toFixed(0)}%
              </span>
            </div>
          ))}
      </div>
    </div>
  )
}
